import { useWindowSize } from 'usehooks-ts';

import {
  MEDIUM_BREAKPOINT,
  SMALL_BREAKPOINT,
} from 'js/design_system/hooks/breakpoints';

export type Breakpoint = 'small' | 'medium' | 'large';

// The thresholds live in `breakpoints` so a caller that classifies width itself can read them
// without importing this module for a value — see that file. Importing them back here keeps
// one source of truth for the numbers.

/**
 * Returns the current breakpoint based on the screen size. These breakpoints match the grid
 * layout breakpoints set in `app/assets/stylesheets/design_system/_breakpoint.scss`.
 *
 * 'small': window width < 568px
 * 'medium': window width >= 568px and < 1280px
 * 'large': window width >= 1280px
 */
export const useBreakpoint = (): Breakpoint => {
  const { width } = useWindowSize();

  if (width < SMALL_BREAKPOINT) {
    return 'small';
  } else if (width < MEDIUM_BREAKPOINT) {
    return 'medium';
  } else {
    return 'large';
  }
};

/**
 * Returns whether the screen size is currently within the specified breakpoints.
 *
 * 'small': screen width < 568px
 * 'medium': screen width >= 568px and < 1280px
 * 'large': screen width >= 1280px
 *
 * Basic usage:
 *
 * @example
 * ```tsx
 * import { useInBreakpoints } from 'js/design_system/hooks/useBreakpoint';
 *
 * const isMobile = useInBreakpoints(['small', 'medium']);
 * ```
 */
export const useInBreakpoints = (breakpoints: Breakpoint[]): boolean => {
  const currentBreakpoint = useBreakpoint();

  return breakpoints.includes(currentBreakpoint);
};
